/**
 * Magana wbs-hero motion
 * Appear springs replay whenever the heading re-enters (animateOnce false, threshold 0).
 * Parallax: y = -scrollY * (speed/100 - 1)
 * Speeds from Framer bundle: sticky BG 120, content 103, heading 90, CTA 80
 * Mobile (<810): parallax disabled on sticky BG (matches Framer override)
 */
(() => {
  const SELECTOR = '[data-wbs-hero]';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function parallaxY(scrollY, speed) {
    if (speed === 100) return 0;
    return -scrollY * (speed / 100 - 1);
  }

  function initHero(root) {
    if (root.dataset.wbsHeroInit === 'true') return;
    root.dataset.wbsHeroInit = 'true';

    const sticky = root.querySelector('[data-wbs-parallax="sticky"]');
    const content = root.querySelector('[data-wbs-parallax="content"]');
    const heading = root.querySelector('[data-wbs-parallax="heading"]');
    const headingText = heading && heading.querySelector('.wbs-hero__heading');
    const headingFill = heading && heading.querySelector('.wbs-hero__heading-fill');
    const cta = root.querySelector('[data-wbs-parallax="cta"]');

    const speedSticky = Number(root.dataset.wbsSpeedSticky || 120);
    const speedContent = Number(root.dataset.wbsSpeedContent || 103);
    const speedHeading = Number(root.dataset.wbsSpeedHeading || 90);
    const speedCta = Number(root.dataset.wbsSpeedCta || 80);

    const laptopMq = window.matchMedia('(min-width: 1200px)');
    const measureSize = 100;

    function measureNaturalWidth(oneLine) {
      if (!headingFill || !headingText) return 0;
      const cs = getComputedStyle(headingText);
      const probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = [
        'position:fixed',
        'left:0',
        'top:0',
        'visibility:hidden',
        'pointer-events:none',
        'display:block',
        'width:max-content',
        'max-width:none',
        'white-space:' + (oneLine ? 'nowrap' : 'normal'),
        'font-family:' + cs.fontFamily,
        'font-weight:' + cs.fontWeight,
        'font-style:' + cs.fontStyle,
        'font-size:' + measureSize + 'px',
        'letter-spacing:-0.01em',
        'text-transform:' + cs.textTransform,
        'line-height:1',
      ].join(';');

      if (oneLine) {
        probe.textContent = (headingFill.textContent || '').replace(/\s+/g, ' ').trim();
      } else {
        probe.innerHTML = headingFill.innerHTML;
      }

      document.body.appendChild(probe);
      const width = probe.getBoundingClientRect().width;
      probe.remove();
      return width;
    }

    function fitHeading() {
      if (!headingText || !heading) return;
      const available = heading.clientWidth;
      if (!available) return;

      const natural = measureNaturalWidth(laptopMq.matches);
      if (!natural) return;

      const size = measureSize * ((available - 1) / natural);
      headingText.style.fontSize = size + 'px';
      headingText.style.lineHeight = size + 'px';
    }

    fitHeading();
    window.addEventListener('resize', fitHeading);
    laptopMq.addEventListener('change', fitHeading);
    if (typeof ResizeObserver === 'function') {
      const headingObserver = new ResizeObserver(fitHeading);
      headingObserver.observe(heading);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitHeading);
    }

    requestAnimationFrame(() => {
      root.classList.add('is-wbs-ready');
    });

    const headingAppear = headingText;
    if (headingAppear && !prefersReducedMotion() && 'IntersectionObserver' in window) {
      const headingObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-wbs-in', entry.isIntersecting);
        });
      }, { threshold: 0 });
      headingObserver.observe(headingAppear);
    } else if (headingAppear) {
      headingAppear.classList.add('is-wbs-in');
    }

    if (prefersReducedMotion()) return;

    let ticking = false;
    const mobileMq = window.matchMedia('(max-width: 809.98px)');
    const desktopScroll = window.matchMedia('(min-width: 990px)');
    let scrollTarget = null;
    let boundLenis = null;

    function readScrollY() {
      if (window.lenis && typeof window.lenis.scroll === 'number') return window.lenis.scroll;
      if (desktopScroll.matches) {
        const page = document.querySelector('.page-wrapper');
        if (page) return page.scrollTop;
      }
      return window.scrollY || window.pageYOffset || 0;
    }

    function apply() {
      const y = readScrollY();
      const disableStickyParallax = mobileMq.matches;

      if (sticky) {
        sticky.style.transform = disableStickyParallax
          ? 'none'
          : `translate3d(0, ${parallaxY(y, speedSticky)}px, 0)`;
      }
      if (content) {
        content.style.transform = `translate3d(0, ${parallaxY(y, speedContent)}px, 0)`;
      }
      if (heading) {
        heading.style.transform = `translate3d(0, ${parallaxY(y, speedHeading)}px, 0)`;
      }
      if (cta) {
        cta.style.transform = `translate3d(0, ${parallaxY(y, speedCta)}px, 0)`;
      }
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }

    function bindScrollTarget() {
      const next = desktopScroll.matches
        ? document.querySelector('.page-wrapper') || window
        : window;
      if (scrollTarget === next) return;
      if (scrollTarget) scrollTarget.removeEventListener('scroll', onScroll);
      scrollTarget = next;
      scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    }

    function bindLenis() {
      if (!window.lenis || boundLenis === window.lenis) return;
      if (boundLenis) boundLenis.off('scroll', onScroll);
      boundLenis = window.lenis;
      window.lenis.on('scroll', onScroll);
    }

    apply();
    bindScrollTarget();
    bindLenis();
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('wbs:lenis', bindLenis);
    if (typeof mobileMq.addEventListener === 'function') {
      mobileMq.addEventListener('change', onScroll);
    }
    desktopScroll.addEventListener('change', () => {
      bindScrollTarget();
      onScroll();
    });
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!(section instanceof Element)) return;
    const hero = section.matches(SELECTOR) ? section : section.querySelector(SELECTOR);
    if (hero) {
      delete hero.dataset.wbsHeroInit;
      initHero(hero);
    }
  });
})();
